import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { catchError, map, of, switchMap, timeout, timer } from 'rxjs';
import { buildApiUrl } from '../config/api.constants';

export interface BuildInfo {
  version: string | null;
  revision: string | null;
}

declare const APP_BUILD_INFO: BuildInfo;

// Direct ng/test invocations work without stamping. npm build/start/watch stamp
// this constant into the bundle, so an old open tab retains its own identity.
export const FRONTEND_BUILD: BuildInfo = typeof APP_BUILD_INFO === 'undefined'
  ? { version: null, revision: null }
  : APP_BUILD_INFO;

export function parseBuildInfo(value: unknown): BuildInfo {
  if (!value || typeof value !== 'object') return { version: null, revision: null };
  const info = value as Record<string, unknown>;
  return {
    version: typeof info['version'] === 'string' ? info['version'].trim() || null : null,
    revision: typeof info['revision'] === 'string' && /^[a-f0-9]{40,64}$/i.test(info['revision'])
      ? info['revision'] : null,
  };
}

export function formatBuildInfo(info: BuildInfo): string {
  const version = info.version?.replace(/-dirty$/, '').replace(/-\d+-g[0-9a-f]+$/i, '');
  if (version && /^[0-9a-f]{7,64}$/i.test(version)
    && info.revision?.toLowerCase().startsWith(version.toLowerCase())) return 'Unavailable';
  return version && /^v?\d+(?:\.\d+)*(?:[-+][0-9a-z.-]+)?$/i.test(version)
    ? version : 'Unavailable';
}

export function buildInfoTitle(info: BuildInfo | undefined): string {
  return [info?.version, info?.revision].filter(Boolean).join(' · ');
}

@Injectable({ providedIn: 'root' })
export class BuildInfoService {
  private readonly http = inject(HttpClient);
  readonly frontend = FRONTEND_BUILD;
  readonly frontendLabel = formatBuildInfo(this.frontend);
  readonly frontendTitle = buildInfoTitle(this.frontend);

  readonly backend = toSignal(timer(0, 60_000).pipe(
    switchMap(() => this.http.get<unknown>(buildApiUrl('/api/version'), {
      cache: 'no-store',
    }).pipe(
      timeout(5_000),
      map(parseBuildInfo),
      catchError(() => of({ version: null, revision: null })),
    )),
  ));
  readonly backendLabel = computed(() => {
    const info = this.backend();
    return info ? formatBuildInfo(info) : 'Loading…';
  });
  readonly backendTitle = computed(() => buildInfoTitle(this.backend()));
}
